import { getCategoryColor } from '@/functions/getCategoryColor.ts';
import { getFacilityColor } from '@/functions/getFacilityColor.ts';
import { getSourceColor } from '@/functions/getSourceColor.ts';
import { useMeQuery } from '@/remote/user-management/fetch-me';
import type { Site } from '@/types/site';

type SortField = keyof Site;
type SortDirection = 'asc' | 'desc';

interface SiteManagementTableProps {
  sites: Site[];
  searchTerm: string;
  sortField: SortField;
  sortDirection: SortDirection;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  filteredSites: Site[];
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onDetail: (site: Site) => void;
  onEdit: (site: Site) => void;
  onManagerSetting: (site: Site) => void;
  onManagePhotos: (site: Site) => void;
  onDelete: (siteId: string) => void;
}

export default function SiteManagementTable({
  sites,
  searchTerm,
  sortField,
  sortDirection,
  currentPage,
  itemsPerPage,
  totalPages,
  startIndex,
  endIndex,
  filteredSites,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onDetail,
  onEdit,
  onManagerSetting,
  onManagePhotos,
  onDelete,
}: Readonly<SiteManagementTableProps>) {
  const { data: me } = useMeQuery();

  function formatCdmaNumber(cdmaNumber: string | undefined): string {
    if (!cdmaNumber) return '-';

    // 000-0000-0000
    return cdmaNumber
      .trim()
      .replace(/-/g, '')
      .replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          현장 관리 목록{` `}
          {searchTerm && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({filteredSites.length}개 결과)
            </span>
          )}
        </h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">페이지당 항목:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-8"
            >
              <option value={5}>5개 항목</option>
              <option value={10}>10개 항목</option>
              <option value={15}>15개 항목</option>
              <option value={20}>20개 항목</option>
              <option value={25}>25개 항목</option>
              <option value={50}>50개 항목</option>
              <option value={filteredSites.length}>
                전체 항목 ({filteredSites.length}개)
              </option>
            </select>
          </div>
          <div className="text-sm text-gray-500">
            {totalPages > 1 ? `총 ${totalPages}페이지` : '1페이지'}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => onSort('siteId')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>현장 ID</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'siteId' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    />
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('managementNumber')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>관리번호</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'managementNumber' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('name')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap w-[200px] truncate"
              >
                <div className="flex items-center space-x-1 w-[200px]">
                  <span>현장명</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'name' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('wellPosition')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 truncate "
              >
                <div className="flex items-center space-x-1">
                  <span>주소</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'wellPosition' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('cdmaNumber')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>CDMA 번호</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'cdmaNumber' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>

              <th
                onClick={() => onSort('managementType')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>관리 분류</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'managementType' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('facilityType')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>시설 유형</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'facilityType' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>
              <th
                onClick={() => onSort('waterIntakeType')}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
              >
                <div className="flex items-center space-x-1">
                  <span>수원 분류</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i
                      className={`ri-arrow-${sortField === 'waterIntakeType' ? (sortDirection === 'asc' ? 'up' : 'down') : 'up-down'}-line text-gray-400`}
                    ></i>
                  </div>
                </div>
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                작업
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sites.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="px-6 py-[150px] text-center text-gray-500 "
                >
                  검색 결과가 없습니다. <br />
                  {(me?.userLevel === 'PUBLIC_OFFICE_MANAGER' ||
                    me?.userLevel === 'TOWN_MANAGER') &&
                    '현장을 추가하려면 SIENG에 문의해주세요.'}
                </td>
              </tr>
            )}
            {sites.map((site) => (
              <tr
                key={site.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onDetail(site);
                }}
              >
                {/* 현장 아이디 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{site.siteId}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {site.managementNumber || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 w-[200px] truncate">
                  <div className="text-sm font-medium text-gray-900 max-w-xs w-[200px] truncate">
                    {site.name}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 truncate">
                    {site.wellPosition?.address || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {parseInt(site?.cdmaNumber || '0') !== 0
                      ? formatCdmaNumber(site?.cdmaNumber?.trim())
                      : '-'}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(site?.managementType)}`}
                  >
                    {site.managementType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFacilityColor(site.facilityType)}`}
                  >
                    {site.facilityType}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSourceColor(site?.waterIntakeType)}`}
                  >
                    {site.waterIntakeType || '-'}
                  </span>
                </td>

                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDetail(site);
                      }}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg cursor-pointer"
                      title="상세 보기"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                    </button>
                    {(me?.userLevel === 'SUPER_ADMINISTRATOR' ||
                      me?.userLevel === 'HEADQUARTERS_MANAGER') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(site);
                        }}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg cursor-pointer"
                        title="수정"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-edit-line"></i>
                        </div>
                      </button>
                    )}
                    {(me?.userLevel === 'SUPER_ADMINISTRATOR' ||
                      me?.userLevel === 'HEADQUARTERS_MANAGER') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onManagerSetting(site);
                        }}
                        className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg cursor-pointer"
                        title="관리자 설정"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-team-line"></i>
                        </div>
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManagePhotos(site);
                      }}
                      className="p-2 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 rounded-lg cursor-pointer"
                      title="현장 사진"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-camera-line"></i>
                      </div>
                    </button>
                    {(me?.userLevel === 'SUPER_ADMINISTRATOR' ||
                      me?.userLevel === 'HEADQUARTERS_MANAGER') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onDelete(site.siteId);
                        }}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg cursor-pointer"
                        title="삭제"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-delete-bin-line"></i>
                        </div>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700">
                {startIndex + 1}부터 {Math.min(endIndex, filteredSites.length)}
                까지 (총 {filteredSites.length}개 결과)
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
                className="px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                title="첫 페이지"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-skip-back-line"></i>
                </div>
              </button>
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center space-x-1"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-s-line"></i>
                </div>
                <span>이전</span>
              </button>
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxVisiblePages / 2)
                  );
                  const endPage = Math.min(
                    totalPages,
                    startPage + maxVisiblePages - 1
                  );

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => onPageChange(1)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span
                          key="start-ellipsis"
                          className="px-2 text-gray-500"
                        >
                          ...
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => onPageChange(i)}
                        className={`px-3 py-2 text-sm border rounded-lg cursor-pointer whitespace-nowrap ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="end-ellipsis" className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => onPageChange(totalPages)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>
              <button
                onClick={() =>
                  onPageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center space-x-1"
              >
                <span>다음</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </div>
              </button>
              <button
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                title="마지막 페이지"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-skip-forward-line"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
